/**
 * Issue #38 — Boundary React qui capture les erreurs de render et affiche
 * une page d'erreur friendly avec errorId + reporting auto au backend.
 *
 * Usage :
 *   ```tsx
 *   <RouteErrorBoundary>
 *     <Suspense fallback={<RouteLoadingFallback/>}>
 *       <Routes>...</Routes>
 *     </Suspense>
 *   </RouteErrorBoundary>
 *   ```
 *
 * Peut être imbriqué (per-route + global). Le boundary le plus interne
 * catch en premier, isolant la page sans démonter le layout.
 */

import { AlertCircle, ArrowLeft, Copy, RotateCw } from "lucide-react"
import { Component, type ErrorInfo, type ReactNode } from "react"

import { reportClientError } from "@/lib/errorReporter"
import { copyText } from "@/lib/clipboard"
import { SUPPORT_EMAIL } from "@/lib/contact"

type Props = {
  children: ReactNode
  /** Optionnel : label de la zone (ex: "tableau de bord", "détail devis"). */
  zoneName?: string
}

type State = {
  hasError: boolean
  errorId: string | null
  reported: boolean
  errorMessage: string
  errorName: string
  stackTrace: string | null
  copyFeedback: string
}

const INITIAL_STATE: State = {
  hasError: false,
  errorId: null,
  reported: false,
  errorMessage: "",
  errorName: "",
  stackTrace: null,
  copyFeedback: "",
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = INITIAL_STATE

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      errorName: error.name || "Error",
      errorMessage: error.message || "(no message)",
      stackTrace: error.stack ?? null,
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Combine le composant stack React au stack JS pour fingerprint plus riche.
    const combinedStack = `${error.stack ?? ""}\n--- React component stack ---${info.componentStack ?? ""}`
    const augmented = new Error(error.message)
    augmented.name = error.name
    augmented.stack = combinedStack

    void reportClientError({
      error: augmented,
      sourceType: "boundary",
    }).then((result) => {
      this.setState({
        errorId: result.errorId,
        reported: result.reported,
      })
    })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleBackHome = () => {
    window.location.href = "/"
  }

  private handleCopyId = () => {
    if (!this.state.errorId) return
    // Pas de toast : son fournisseur peut être cassé.
    // Le retour s'écrit sous le bouton, succès comme échec.
    // comply-ignore: async-await-only — convention du dépôt : .then/.catch, jamais async/await.
    copyText(this.state.errorId)
      .then(() => this.setState({ copyFeedback: "Copié" }))
      .catch(() => this.setState({ copyFeedback: "Copie impossible : sélectionnez l'identifiant." }))
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    const { errorId, reported, errorMessage, errorName, stackTrace, copyFeedback } = this.state

    return (
      <div
        role="alert"
        className="flex min-h-[60vh] w-full items-center justify-center p-6"
        data-test="route-error-boundary"
      >
        <div className="w-full max-w-2xl rounded-lg border border-destructive/30 bg-card p-8 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-1 h-6 w-6 shrink-0 text-destructive" aria-hidden="true" />
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-foreground">
                Une erreur s'est produite
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Quelque chose s'est mal passé pendant l'affichage de
                {this.props.zoneName ? ` ${this.props.zoneName}` : " cette page"}.
                Aucune donnée n'a été perdue.
              </p>

              {errorId ? (
                <div className="mt-4 rounded-md border bg-muted/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <code
                      className="select-all font-mono text-xs text-foreground"
                      data-test="error-id"
                    >
                      {errorId}
                    </code>
                    <button
                      type="button"
                      onClick={this.handleCopyId}
                      className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-accent"
                      data-test="copy-error-id"
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      Copier l'ID
                    </button>
                  </div>
                  <span
                    id="error-id-copy-feedback"
                    aria-live="polite"
                    className="block pt-1 text-xs text-muted-foreground"
                  >
                    {copyFeedback}
                  </span>
                </div>
              ) : null}

              <p
                className={`mt-3 text-sm ${reported ? "text-emerald-700" : "text-amber-700"}`}
                data-test="error-banner"
              >
                {reported
                  ? "L'équipe de Qualneo a été notifiée."
                  : `Notez l'ID de l'erreur et envoyez-le à ${SUPPORT_EMAIL}.`}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={this.handleReload}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  data-test="error-reload"
                >
                  <RotateCw className="h-4 w-4" aria-hidden="true" />
                  Recharger la page
                </button>
                <button
                  type="button"
                  onClick={this.handleBackHome}
                  className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-accent"
                  data-test="error-back-home"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Retour à l'accueil
                </button>
              </div>

              <details
                className="mt-5 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground"
                open={import.meta.env.DEV}
              >
                <summary className="cursor-pointer select-none text-xs font-medium">
                  Détails techniques
                </summary>
                <p className="mt-2">
                  <strong>{errorName}</strong> : {errorMessage}
                </p>
                {stackTrace ? (
                  <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-[10px] leading-snug">
                    {stackTrace}
                  </pre>
                ) : null}
              </details>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
